import { Concept } from '../../../../models/Entities/Contracts/Concept';
import { ConceptCategory } from '../../../../models/Entities/Contracts/ConceptCategory';
import { NewConcept } from '../../../../models/Entities/Contracts/NewConcept';
import { AppState } from '../../../../redux/reducers';
import * as ConceptStore from '../../../../redux/middleware/ConceptMiddleware';
import * as ConceptCategoryStore from '../../../../redux/middleware/ConceptCategoryMiddleware';
import * as ConceptSponsorStore from '../../../../redux/middleware/ConceptSponsorMiddleware';

export interface ConceptValuesFormOwnProps {
  concept?: Concept;
  sponsorId?: string;
  onSuccess?: () => void;
  isOpen: boolean;
  dismissPanel: () => void;
}

interface ConnectedProps {
  isUpdating: boolean;
  successUpdating: boolean;
  isAdding: boolean;
  successAdding: boolean;
  errorMessage?: string;
  categories: ConceptCategory[];
  isLoadingCategories: boolean;
  concepts: Concept[];
}

interface DispatchProps {
  UpdateConcept: typeof ConceptStore.actionCreators.UpdateConcept;
  AddConcept: typeof ConceptStore.actionCreators.AddConcept;
  AddConceptSponsor: typeof ConceptSponsorStore.actionCreators.AddConceptSponsor;
  GetAllConceptCategories: typeof ConceptCategoryStore.actionCreators.GetAllConceptCategories;
}

export type Props = ConceptValuesFormOwnProps & ConnectedProps & DispatchProps;

export interface FormValues {
  conceptName: string;
  conceptDescription: string;
  conceptCode: string;
  conceptCategoryId?: number;
  costCurrency: string;
  referenceCost: string;
  sponsorWeigthedCost: string;
  siteWeigthedCost: string;
  countryWeigthedCost: string;
  globalWeigthedCost: string;
}

export const initFormValues = (concept?: Concept): FormValues => ({
  conceptName: concept?.conceptName || '',
  conceptDescription: concept?.conceptDescription || '',
  conceptCode: concept?.conceptCode || '',
  conceptCategoryId: concept?.conceptCategoryId,
  costCurrency: concept?.costCurrency || '',
  referenceCost: concept?.referenceCost != null ? String(concept.referenceCost) : '',
  sponsorWeigthedCost: concept?.sponsorWeigthedCost != null ? String(concept.sponsorWeigthedCost) : '',
  siteWeigthedCost: concept?.siteWeigthedCost != null ? String(concept.siteWeigthedCost) : '',
  countryWeigthedCost: concept?.countryWeigthedCost != null ? String(concept.countryWeigthedCost) : '',
  globalWeigthedCost: concept?.globalWeigthedCost != null ? String(concept.globalWeigthedCost) : ''
});

const parseOptionalNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const num = parseFloat(trimmed);
  return isNaN(num) ? undefined : num;
};

export const buildNewConcept = (values: FormValues): NewConcept => ({
  conceptName: values.conceptName.trim(),
  conceptDescription: values.conceptDescription.trim(),
  conceptReferenceId: '',
  conceptCode: values.conceptCode.trim() || undefined,
  conceptCategoryId: values.conceptCategoryId,
  costCurrency: values.costCurrency || undefined,
  referenceCost: parseOptionalNumber(values.referenceCost),
  sponsorWeigthedCost: parseOptionalNumber(values.sponsorWeigthedCost),
  siteWeigthedCost: parseOptionalNumber(values.siteWeigthedCost),
  countryWeigthedCost: parseOptionalNumber(values.countryWeigthedCost),
  globalWeigthedCost: parseOptionalNumber(values.globalWeigthedCost)
});

export const buildUpdatedConcept = (concept: Concept, values: FormValues): Concept => ({
  ...concept,
  conceptName: values.conceptName.trim(),
  conceptDescription: values.conceptDescription.trim(),
  conceptCode: values.conceptCode.trim() || undefined,
  conceptCategoryId: values.conceptCategoryId,
  costCurrency: values.costCurrency,
  referenceCost: parseOptionalNumber(values.referenceCost),
  sponsorWeigthedCost: parseOptionalNumber(values.sponsorWeigthedCost),
  siteWeigthedCost: parseOptionalNumber(values.siteWeigthedCost),
  countryWeigthedCost: parseOptionalNumber(values.countryWeigthedCost),
  globalWeigthedCost: parseOptionalNumber(values.globalWeigthedCost)
});

export interface FormValidation {
  success: boolean;
  errors: string[];
}

export const validateForm = (values: FormValues, t: (key: string, fallback: string) => string): FormValidation => {
  const errors: string[] = [];
  if (!values.conceptName.trim()) {
    errors.push(t('conceptValuesForm.validation.nameRequired', 'El nombre es obligatorio'));
  }
  return { success: errors.length === 0, errors };
};

export const mapStateToProps = (state: AppState) => ({
  isUpdating: Boolean(state.Concepts?.isUpdatingOne),
  successUpdating: Boolean(state.Concepts?.successUpdatingOne),
  isAdding: Boolean(state.Concepts?.isAddingNewOne),
  successAdding: Boolean(state.Concepts?.successAddingNewOne),
  errorMessage: state.Concepts?.error?.ErrorMessage,
  categories: state.ConceptCategories?.list || [],
  isLoadingCategories: Boolean(state.ConceptCategories?.isLoadingAll),
  concepts: state.Concepts?.list || []
});

export const mapDispatchToProps = {
  UpdateConcept: ConceptStore.actionCreators.UpdateConcept,
  AddConcept: ConceptStore.actionCreators.AddConcept,
  AddConceptSponsor: ConceptSponsorStore.actionCreators.AddConceptSponsor,
  GetAllConceptCategories: ConceptCategoryStore.actionCreators.GetAllConceptCategories
};
